import { X, CheckCircle, AlertCircle, Info, AlertTriangle, Loader2 } from 'lucide-react';
import { useToastStore, type Toast as ToastType } from '../../stores/toastStore';

// Simple utility for class joining since lib/utils doesn't exist
const clsx = (...classes: (string | undefined | null | false)[]) => classes.filter(Boolean).join(' ');

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
  loading: Loader2,
};

const styles = {
  success: 'bg-green-50 dark:bg-green-500/15 border-green-200 dark:border-green-500/30 text-green-800 dark:text-green-200',
  error: 'bg-red-50 dark:bg-red-500/15 border-red-200 dark:border-red-500/30 text-red-800 dark:text-red-200',
  info: 'bg-blue-50 dark:bg-blue-500/15 border-blue-200 dark:border-blue-500/30 text-blue-800 dark:text-blue-200',
  warning: 'bg-yellow-50 dark:bg-yellow-500/15 border-yellow-200 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-200',
  loading: 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100',
};

const iconStyles = {
  success: 'text-green-500',
  error: 'text-red-500',
  info: 'text-blue-500',
  warning: 'text-yellow-500',
  loading: 'text-blue-500 animate-spin',
};

export function Toast({ id, type, title, message }: ToastType) {
  const removeToast = useToastStore((state) => state.removeToast);
  const Icon = icons[type];

  return (
    <div
      className={clsx(
        'flex items-start gap-3 w-80 p-4 rounded-lg border shadow-lg transition-all animate-in slide-in-from-right-full backdrop-blur-sm',
        styles[type]
      )}
      role="alert"
    >
      <Icon className={clsx('w-5 h-5 mt-0.5', iconStyles[type])} />
      <div className="flex-1">
        {title && <h4 className="font-semibold text-sm mb-1">{title}</h4>}
        <p className="text-sm opacity-90 whitespace-pre-line">{message}</p>
      </div>
      <button
        onClick={() => removeToast(id)}
        className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts);

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      <div className="pointer-events-auto flex flex-col gap-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} />
        ))}
      </div>
    </div>
  );
}
